import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  CheckCircleOutline,
  ContentCopy,
  ErrorOutline,
  HourglassEmpty,
  Refresh,
  Search,
  TokenOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import GenerationJobDetailModal from "./GenerationJobDetailModal";

const BRAND = "#6D28D9";

function initials(name, email) {
  const s = String(name || email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function formatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}m ${s}s`;
}

function Donut({ slices, totalLabel }) {
  const total = slices.reduce((a, s) => a + s.count, 0) || 1;
  let cursor = 0;
  const stops = slices
    .map((s) => {
      const start = cursor;
      const pct = (s.count / total) * 100;
      cursor += pct;
      return `${s.color} ${start}% ${cursor}%`;
    })
    .join(", ");
  return (
    <Box sx={{ position: "relative", width: 140, height: 140, mx: "auto" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: `conic-gradient(${stops || `${BRAND} 0 100%`})`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 28,
          borderRadius: "50%",
          bgcolor: "background.paper",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={800}>
          {totalLabel ?? total}
        </Typography>
      </Box>
    </Box>
  );
}

function SummaryCard({ icon, title, value, sub, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: alpha(color, 0.12), color }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {sub}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function BreakdownCard({ title, children, totalJobs }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          {title}
        </Typography>
        <Chip size="small" label={`+ ${totalJobs} jobs`} sx={{ bgcolor: alpha("#2563EB", 0.1), color: "#2563EB", fontWeight: 700 }} />
      </Stack>
      {children}
    </Paper>
  );
}

const STATUS_COLOR = {
  completed: "#16A34A",
  failed: "#DC2626",
  processing: "#EA580C",
  other: "#64748B",
};

const GenerationMonitor = ({ lang }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [studio, setStudio] = React.useState("all");
  const [status, setStatus] = React.useState("all");
  const [feature, setFeature] = React.useState("all");
  const [qInput, setQInput] = React.useState("");
  const [q, setQ] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [detailJobId, setDetailJobId] = React.useState(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setQ(qInput);
      setPage(0);
    }, 350);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        studio,
        status,
        feature,
      });
      if (q.trim()) params.set("q", q.trim());
      const res = await hitAxios({
        path: `/api/admin/generation_monitor?${params.toString()}`,
        post: false,
        admin: true,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) setData(res.data.data);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, page, rowsPerPage, studio, status, feature, q]);

  React.useEffect(() => {
    load();
    const id = window.setInterval(load, 15000);
    return () => window.clearInterval(id);
  }, [load]);

  const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  const total = Number(data?.total || 0);

  const featureOptions = React.useMemo(() => {
    const fromBreakdown = (data?.by_feature || []).map((f) => f.label).filter(Boolean);
    const fromJobs = jobs.map((j) => j.feature).filter(Boolean);
    return Array.from(new Set([...fromBreakdown, ...fromJobs, feature].filter((f) => f && f !== "all"))).sort();
  }, [data, jobs, feature]);

  const summary = data?.summary || {};
  const byStatus = (data?.by_status || []).map((s) => ({
    ...s,
    color: STATUS_COLOR[s.label] || STATUS_COLOR.other,
  }));
  const byStudio = (data?.by_studio || []).map((s, i) => ({
    ...s,
    color: i === 0 ? BRAND : "#2563EB",
  }));
  const totalJobs =
    (data?.by_status || []).reduce((a, r) => a + Number(r.count || 0), 0) || 0;

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id));
    } catch {
      /* ignore */
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {lang?.watchDogTitle || lang?.generationMonitorTitle || "WatchDog"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lang?.watchDogSub ||
              lang?.generationMonitorSub ||
              "Real-time job queue — every generation request across all studios."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={load}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 700, alignSelf: "flex-start" }}
        >
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard
            icon={<HourglassEmpty />}
            color="#CA8A04"
            title="Queue Now"
            value={`${summary.queue_now ?? 0} Processing`}
            sub="Live in-flight jobs"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard
            icon={<CheckCircleOutline />}
            color="#16A34A"
            title="Completed Today"
            value={summary.completed_today ?? 0}
            sub="Since midnight"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard
            icon={<ErrorOutline />}
            color="#DC2626"
            title="Failed Today"
            value={summary.failed_today ?? 0}
            sub={`Auto-refunded: ${summary.refunds_today ?? 0}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard
            icon={<HourglassEmpty />}
            color="#2563EB"
            title="Avg Queue Time"
            value="—"
            sub="Target <5s"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard
            icon={<TokenOutlined />}
            color="#DC2626"
            title="Refunds Issued"
            value={summary.refunds_today ?? 0}
            sub="Today"
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}>
          <BreakdownCard title="By Status" totalJobs={totalJobs}>
            <Donut slices={byStatus} />
            <Stack spacing={0.75} sx={{ mt: 2 }}>
              {byStatus.map((s) => (
                <Stack key={s.label} direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700} sx={{ color: s.color }}>
                    {s.label} · {s.pct}%
                  </Typography>
                  <Typography variant="caption">{s.count}</Typography>
                </Stack>
              ))}
            </Stack>
          </BreakdownCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <BreakdownCard title="By Studio" totalJobs={totalJobs}>
            <Donut slices={byStudio} />
            <Stack spacing={0.75} sx={{ mt: 2 }}>
              {byStudio.map((s) => (
                <Stack key={s.label} direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: "capitalize" }}>
                    {s.label} · {s.pct}%
                  </Typography>
                  <Typography variant="caption">{s.count}</Typography>
                </Stack>
              ))}
            </Stack>
          </BreakdownCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <BreakdownCard title="By Feature" totalJobs={totalJobs}>
            <Stack spacing={1.1}>
              {(data?.by_feature || []).map((f) => (
                <Box key={f.label}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: "70%" }}>
                      {f.label}
                    </Typography>
                    <Typography variant="caption">
                      {f.count} · {f.pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Number(f.pct) || 0)}
                    sx={{ height: 6, borderRadius: 99, bgcolor: alpha(BRAND, 0.08), "& .MuiLinearProgress-bar": { bgcolor: BRAND } }}
                  />
                </Box>
              ))}
            </Stack>
          </BreakdownCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <BreakdownCard title="By Model" totalJobs={totalJobs}>
            <Stack spacing={1.1}>
              {(data?.by_model || []).map((f) => (
                <Box key={f.label}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: "70%" }}>
                      {f.label}
                    </Typography>
                    <Typography variant="caption">
                      {f.count} · {f.pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Number(f.pct) || 0)}
                    sx={{ height: 6, borderRadius: 99, bgcolor: alpha("#2563EB", 0.08), "& .MuiLinearProgress-bar": { bgcolor: "#2563EB" } }}
                  />
                </Box>
              ))}
            </Stack>
          </BreakdownCard>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ md: "center" }}
          sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ mr: 1 }}>
            Live Job Queue
          </Typography>
          <Box sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Studio</InputLabel>
            <Select
              label="Studio"
              value={studio}
              onChange={(e) => {
                setStudio(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Studios</MenuItem>
              <MenuItem value="image">Image</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Feature</InputLabel>
            <Select
              label="Feature"
              value={feature}
              onChange={(e) => {
                setFeature(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Features</MenuItem>
              {featureOptions.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search jobs, users, models..."
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 240 } }}
          />
        </Stack>

        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>JOB ID</TableCell>
                <TableCell>USER</TableCell>
                <TableCell>STUDIO</TableCell>
                <TableCell>MODEL</TableCell>
                <TableCell>FEATURE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>GEN TIME</TableCell>
                <TableCell>CREDITS</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={10}>Loading…</TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>No jobs match.</TableCell>
                </TableRow>
              ) : (
                jobs.map((j) => (
                  <TableRow key={String(j.job_id)} hover>
                    <TableCell>{j.n}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          #{String(j.job_id).slice(0, 10)}
                        </Typography>
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={() => copyId(j.job_id)}>
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: alpha(BRAND, 0.15), color: BRAND }}>
                          {initials(j.user_name, j.user_email)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600}>
                          {j.user_name || j.user_email || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={String(j.studio || "").toUpperCase()}
                        sx={{ bgcolor: alpha(BRAND, 0.12), color: BRAND, fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                        {j.model || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{j.feature || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: STATUS_COLOR[j.status] || STATUS_COLOR.other,
                          }}
                        />
                        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                          {j.status}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDuration(j.gen_sec)}</TableCell>
                    <TableCell>
                      {j.credits != null ? (
                        <Chip
                          size="small"
                          icon={<TokenOutlined sx={{ fontSize: "14px !important" }} />}
                          label={j.credits}
                          sx={{ bgcolor: alpha(BRAND, 0.12), color: BRAND, fontWeight: 800 }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={lang?.view || "View"}>
                        <IconButton size="small" onClick={() => setDetailJobId(j.job_id)}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <GenerationJobDetailModal
        open={Boolean(detailJobId)}
        jobId={detailJobId}
        onClose={() => setDetailJobId(null)}
        lang={lang}
      />
    </Box>
  );
};

export default GenerationMonitor;
